import { useQuery, useMutation } from '@tanstack/react-query';
import type {
  BookingQuoteRequest,
  BookingQuoteResponse,
  BookingConfirmationDTO,
  BookingLookupResponse,
  ModifyRequoteResponse,
} from '@pms/shared';
import {
  getQuote, confirmBooking, getBooking, resendConfirmation,
  cancelBooking, requoteBooking, modifyBooking,
  type ConfirmBookingBody,
} from '../services/api/bookings.api';

/** Server-authoritative re-quote for the chosen stay. */
export function useBookingQuote(body: BookingQuoteRequest, enabled: boolean) {
  return useQuery<BookingQuoteResponse>({
    queryKey: ['booking-quote', body],
    queryFn: () => getQuote(body),
    enabled,
    retry: false,
  });
}

/** Confirm mutation; the caller supplies the per-attempt Idempotency-Key. */
export function useConfirmBooking() {
  return useMutation<BookingConfirmationDTO, unknown, { body: ConfirmBookingBody; idempotencyKey: string }>({
    mutationFn: ({ body, idempotencyKey }) => confirmBooking(body, idempotencyKey),
  });
}

/** Cold-load lookup (ref + PIN); a mutation since the PIN is user-supplied. */
export function useBookingLookup() {
  return useMutation<BookingLookupResponse, unknown, { ref: string; pin: string }>({
    mutationFn: ({ ref, pin }) => getBooking(ref, pin),
  });
}

/** Resend the confirmation email (ref + PIN). */
export function useResendConfirmation() {
  return useMutation<void, unknown, { ref: string; pin: string }>({
    mutationFn: ({ ref, pin }) => resendConfirmation(ref, pin),
  });
}

/** Cancel a booking (ref + PIN). */
export function useCancelBooking() {
  return useMutation<BookingLookupResponse, unknown, { ref: string; pin: string; reason?: string }>({
    mutationFn: ({ ref, pin, reason }) => cancelBooking(ref, pin, reason),
  });
}

/** Read-only requote for a date change. */
export function useRequote() {
  return useMutation<ModifyRequoteResponse, unknown, { ref: string; pin: string; checkIn: string; checkOut: string }>({
    mutationFn: ({ ref, pin, checkIn, checkOut }) => requoteBooking(ref, pin, checkIn, checkOut),
  });
}

/** Apply a date change (ref + PIN). */
export function useModifyBooking() {
  return useMutation<BookingLookupResponse, unknown, { ref: string; pin: string; checkIn: string; checkOut: string; quotedTotal: number }>({
    mutationFn: ({ ref, pin, checkIn, checkOut, quotedTotal }) => modifyBooking(ref, { pin, checkIn, checkOut, quotedTotal }),
  });
}
